import React, { useState, useEffect, useRef } from "react";
import "./FormAkun.css";
import Icon from "@mdi/react";
import { mdiPencil, mdiEye, mdiEyeOff } from "@mdi/js";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const EXPRESS_API = process.env.REACT_APP_EXPRESS_API;

const FormAkun = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nama: "",
    telegramId: "",
    email: "",
    password: "********",
    confirmPassword: "",
  });

  const [editableField, setEditableField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const inputRefs = useRef({});

  // 🧩 Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${EXPRESS_API}/api/users/current`, {
          method: "GET",
          credentials: "include",
        });
        if (res.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        const data = await res.json();
        setFormData({
          _id: data.user?._id || "",
          nama: data.user?.nama || "",
          email: data.user?.email || "",
          telegramId: data.user?.telegramId || "",
          password: "********",
          confirmPassword: "",
        });
      } catch (err) {
        console.error("Error fetching user:", err);
        Swal.fire({
          icon: "error",
          title: "Gagal Mengambil Data",
          text: "Terjadi kesalahan saat mengambil data pengguna.",
          confirmButtonColor: "#3C5896",
        });
      }
    };
    fetchUser();
  }, [navigate]);

  // 🧭 Input handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✏️ Edit mode + fokus
  const handleEdit = (fieldName) => {
    setEditableField(fieldName);
    setTimeout(() => {
      inputRefs.current[fieldName]?.focus();
    }, 100);
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editableField === "password") {
      if (formData.password !== formData.confirmPassword) {
        Swal.fire({
          icon: "error",
          title: "Password Tidak Cocok",
          text: "Konfirmasi password baru tidak sesuai.",
          confirmButtonColor: "#3C5896",
        });
        return;
      }
    }

    setEditableField(null);

    const payload = {
      nama: formData.nama,
      telegramId: formData.telegramId,
      email: formData.email,
    };

    try {
      const res = await fetch(
        `${EXPRESS_API}/api/users/update/${formData._id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Gagal update data pengguna");

      Swal.fire({
        icon: "success",
        title: "Data Berhasil Disimpan!",
        text: "Perubahan Anda telah disimpan.",
        confirmButtonColor: "#3C5896",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Gagal Menyimpan Data",
        text: err.message,
      });
    }
  };

  return (
    <div className="form-container">
      <h3> Update Data Pengguna</h3>
      <form onSubmit={handleSubmit}>
        {/* Nama */}
        <div className="form-group">
          <label>Nama</label>
          <div className="input-wrapper">
            <input
              ref={(el) => (inputRefs.current["nama"] = el)}
              type="text"
              name="nama"
              value={formData.nama}
              onChange={handleChange}
              disabled={editableField !== "nama"}
              className={editableField === "nama" ? "editable" : ""}
            />
            <Icon
              path={mdiPencil}
              size={0.8}
              className="edit-icon"
              onClick={() => handleEdit("nama")}
            />
          </div>
        </div>

        {/* No Telegram */}
        <div className="form-group">
          <label>ID Telegram</label>
          <div className="input-wrapper">
            <input
              ref={(el) => (inputRefs.current["no_telegram"] = el)}
              type="text"
              name="no_telegram"
              value={formData.no_telegram}
              onChange={handleChange}
              disabled={editableField !== "no_telegram"}
              className={editableField === "no_telegram" ? "editable" : ""}
            />
            <Icon
              path={mdiPencil}
              size={0.8}
              className="edit-icon"
              onClick={() => handleEdit("no_telegram")}
            />
          </div>
        </div>

        {/* Email */}
        <div className="form-group">
          <label>Email</label>
          <div className="input-wrapper">
            <input
              ref={(el) => (inputRefs.current["email"] = el)}
              type="text"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={editableField !== "email"}
              className={editableField === "email" ? "editable" : ""}
            />
            <Icon
              path={mdiPencil}
              size={0.8}
              className="edit-icon"
              onClick={() => handleEdit("email")}
            />
          </div>
        </div>

        {/* Password */}
        <div className="form-group">
          <label>Kata Sandi</label>
          <div className="input-wrapper" style={{ position: "relative" }}>
            <input
              ref={(el) => (inputRefs.current["password"] = el)}
              type={showPassword ? "text" : "password"}
              name="password"
              value={
                editableField === "password" ? formData.password : "********"
              }
              onChange={handleChange}
              disabled={editableField !== "password"}
              className={editableField === "password" ? "editable" : ""}
              placeholder={
                editableField === "password" ? "Masukkan kata sandai baru..." : ""
              }
            />

            {/* 👁️ Ikon Mata untuk Password */}
            {editableField === "password" && (
              <Icon
                path={showPassword ? mdiEyeOff : mdiEye}
                size={0.8}
                onClick={toggleShowPassword}
                style={{
                  position: "absolute",
                  right: "35px",
                  top: "12px",
                  cursor: "pointer",
                  color: showPassword ? "#3C5896" : "#555",
                  transition: "color 0.3s",
                  zIndex: 2,
                }}
              />
            )}

            {/* ✏️ Ikon Edit */}
            <Icon
              path={mdiPencil}
              size={0.8}
              className="edit-icon"
              onClick={() => handleEdit("password")}
            />
          </div>
        </div>

        <button type="submit" className="save-button">
          Simpan
        </button>
      </form>
    </div>
  );
};

export default FormAkun;
